import type {
  SaleOrderInfo,
  SaleOrderLine,
  SaleOutwardHeader,
  ScannedSerialRow,
} from "../types";
import { affectsBhmrAutoLink, applyBhmrAutoLink, withBhmrCardFlags } from "./bhmrAutoLink";
import { findOrderLineForRow } from "./selectors";

/**
 * Toàn bộ dữ liệu phiếu xuất bán nằm trong một state duy nhất.
 * Trang cũ giữ danh sách serial ở 3 mảng song song (dataSerialOnHand, checkDuplicateCode,
 * checkDuplicatePositionId) và sửa từ 4 chỗ khác nhau nên dễ lệch nhau; ở đây mọi thay đổi
 * đi qua reducer.
 */
export interface SaleOutwardState {
  header: SaleOutwardHeader;
  order: SaleOrderInfo;
  orderLines: SaleOrderLine[];
  /** Serial đã quét, theo thứ tự quét. Serial là khoá duy nhất của dòng */
  rows: ScannedSerialRow[];
  /** Id phiếu sau lần lưu đầu tiên; 0 khi chưa lưu */
  transactionId: number;
}

export type SaleOutwardAction =
  | { type: "RESET"; header: SaleOutwardHeader }
  | {
      type: "LOAD_TEMPLATE";
      order: SaleOrderInfo;
      orderLines: SaleOrderLine[];
      headerPatch: Partial<SaleOutwardHeader>;
    }
  | {
      type: "LOAD_DETAIL";
      header: SaleOutwardHeader;
      order: SaleOrderInfo;
      orderLines: SaleOrderLine[];
      rows: ScannedSerialRow[];
    }
  | { type: "SET_HEADER_FIELD"; key: keyof SaleOutwardHeader; value: any }
  | { type: "ADD_ROWS"; rows: ScannedSerialRow[] }
  | { type: "REMOVE_ROWS"; serials: string[] }
  | { type: "SET_ROW_QUANTITY"; serial: string; quantity: number }
  | { type: "SET_ROW_NOTE"; serial: string; note: string | null }
  | { type: "LINK_DEVICE"; cardSerial: string; deviceSerial: string }
  | { type: "UNLINK_DEVICE"; cardSerial: string }
  /** Xuất đổi trả: "Serial nhập" của một serial xuất; mảng rỗng = người dùng bỏ chọn, không điền sẵn lại */
  | { type: "SET_EXCHANGE_SERIALS"; serial: string; oldSerials: string[] }
  | { type: "SAVED"; transactionId: number }
  | { type: "LOCKED"; transactionId: number; finishDate: string };

export const createEmptyHeader = (
  sourceType: string,
  sourceCode: string,
): SaleOutwardHeader => ({
  transactionId: 0,
  transactionCode: "",
  transactionDate: "",
  sourceCode,
  sourceType,
  subInvId: null,
  departmentId: null,
  status: "NEW",
  description: "",
  attribute11: "",
  attribute12: "",
});

export const createInitialState = (
  sourceType: string,
  sourceCode: string,
): SaleOutwardState => ({
  header: createEmptyHeader(sourceType, sourceCode),
  order: {},
  orderLines: [],
  rows: [],
  transactionId: 0,
});

const rowKey = (row: ScannedSerialRow) => (row.serial ?? "").trim();

/**
 * Cờ "không serial" có khi chỉ nằm ở dòng đơn cùng mã hàng; gắn luôn vào dòng serial lúc nhận
 * để lưới, hộp chọn thiết bị và LINK_DEVICE cùng dựa một nguồn.
 */
const withNoSerialFlag = (row: ScannedSerialRow, orderLines: SaleOrderLine[]): ScannedSerialRow =>
  row.isNoSerial === true || findOrderLineForRow(orderLines, row)?.isNoSerial !== true
    ? row
    : { ...row, isNoSerial: true };
const linkKey = (row: ScannedSerialRow) => (row.linkedDeviceSerial ?? "").trim();
/** Cờ không serial + cờ thẻ BHMR suy từ dòng đơn */
const prepareRow = (row: ScannedSerialRow, orderLines: SaleOrderLine[]) =>
  withBhmrCardFlags(withNoSerialFlag(row, orderLines), orderLines);

const normalizeExchangeSerial = (value: string | null | undefined) => (value ?? "").trim().toUpperCase();

/**
 * Xuất đổi trả, một đổi một: điền sẵn "Serial nhập" cho các dòng chưa có (undefined / null), mỗi dòng đúng một serial trả
 * còn trống (chưa dòng nào trên phiếu chọn). Ưu tiên serial trong "Serial đổi" (ATTRIBUTE22) của dòng đơn cùng mã hàng,
 * không có thì đơn chỉ có một serial trả mới lấy. Chỉ là gợi ý, người dùng sửa trong hộp chọn; mảng rỗng là đã bỏ chọn,
 * giữ nguyên. Không có gì thì để trống, backend tự ghép theo loại trừ
 */
const applyExchangeDefaults = (rows: ScannedSerialRow[], state: SaleOutwardState): ScannedSerialRow[] => {
  if (state.header.sourceType !== "XUAT_DOI_TRA") return rows;
  // Serial trả -> mã hàng, để ưu tiên ghép cùng mã hàng (máy đổi máy, thẻ đổi thẻ)
  const itemByReturnSerial = new Map<string, string>();
  (state.order.returnSerials ?? []).forEach((item) => {
    const key = normalizeExchangeSerial(item.serial);
    if (key && !itemByReturnSerial.has(key)) itemByReturnSerial.set(key, normalizeExchangeSerial(item.itemCode));
  });
  if (!itemByReturnSerial.size) return rows;
  // Serial vừa nhập về vừa xuất lại trên phiếu này (thẻ BHMR cấp lại cùng số): không phải đổi, không điền và không làm serial nhập
  const reissued = new Set(rows.map((row) => normalizeExchangeSerial(row.serial)).filter((s) => itemByReturnSerial.has(s)));
  const used = new Set<string>();
  rows.forEach((row) => (row.exchangeOldSerials ?? []).forEach((old) => used.add(normalizeExchangeSerial(old))));
  const isFree = (serial: string) => itemByReturnSerial.has(serial) && !used.has(serial) && !reissued.has(serial);
  let changed = false;
  const next = rows.map((row) => {
    if (row.exchangeOldSerials != null || row.isNoSerial || reissued.has(normalizeExchangeSerial(row.serial))) return row;
    const itemCode = normalizeExchangeSerial(row.itemCode);
    const sameItem = (serial: string) => itemByReturnSerial.get(serial) === itemCode;
    const fromLine = (findOrderLineForRow(state.orderLines, row)?.attribute22 ?? "")
      .split(",")
      .map(normalizeExchangeSerial)
      .filter(isFree);
    const free = Array.from(itemByReturnSerial.keys()).filter(isFree);
    const freeSameItem = free.filter(sameItem);
    // Không có gợi ý từ dòng đơn thì chỉ điền khi còn đúng một lựa chọn (cùng mã hàng, hoặc còn đúng một serial trả)
    const picked =
      fromLine.find(sameItem) ??
      fromLine[0] ??
      (freeSameItem.length === 1 ? freeSameItem[0] : free.length === 1 ? free[0] : null);
    if (!picked) return row;
    used.add(picked);
    changed = true;
    return { ...row, exchangeOldSerials: [picked] };
  });
  return changed ? next : rows;
};

export const saleOutwardReducer = (
  state: SaleOutwardState,
  action: SaleOutwardAction,
): SaleOutwardState => {
  switch (action.type) {
    case "RESET":
      return { ...createInitialState("", ""), header: action.header };

    case "LOAD_TEMPLATE":
      return {
        ...state,
        order: action.order,
        orderLines: action.orderLines,
        header: { ...state.header, ...action.headerPatch },
      };

    case "LOAD_DETAIL": {
      const loaded: SaleOutwardState = {
        ...state,
        header: action.header,
        order: action.order,
        orderLines: action.orderLines,
        transactionId: Number(action.header.transactionId) || 0,
      };
      const isLocked = action.header?.status === "LOCK";
      // Liên kết đã lưu (TBL_BHMR_SERIAL_LINK, TBL_SERIAL_EXCHANGE_LINK) về sẵn trên dòng. Phiếu đã khoá thì hiện đúng như DB,
      // không tự gắn / điền sẵn thêm
      const prepared = action.rows.map((row) => prepareRow(row, action.orderLines));
      const rows = isLocked ? prepared : applyExchangeDefaults(prepared, loaded);
      return { ...loaded, rows: isLocked ? rows : applyBhmrAutoLink(rows, action.orderLines) };
    }

    case "SET_HEADER_FIELD":
      return { ...state, header: { ...state.header, [action.key]: action.value } };

    case "ADD_ROWS": {
      // Chặn trùng ngay trong reducer, phòng khi hai lượt quét cùng serial về gần nhau
      const existing = new Set(state.rows.map(rowKey));
      const added: ScannedSerialRow[] = [];
      action.rows.forEach((row) => {
        const key = rowKey(row);
        if (!key || existing.has(key)) return;
        existing.add(key);
        added.push(prepareRow(row, state.orderLines));
      });
      if (!added.length) return state;
      const rows = applyExchangeDefaults([...state.rows, ...added], state);
      // state.rows đã là điểm dừng của applyBhmrAutoLink; dòng mới không phải thẻ / máy được gói nhắc tới thì kết quả giữ nguyên
      const relink = added.some((row) => affectsBhmrAutoLink(row, state.orderLines));
      return { ...state, rows: relink ? applyBhmrAutoLink(rows, state.orderLines) : rows };
    }

    case "REMOVE_ROWS": {
      const toRemove = new Set(action.serials.map((serial) => serial.trim()));
      const kept = state.rows.filter((row) => !toRemove.has(rowKey(row)));
      if (kept.length === state.rows.length) return state;
      // Xoá dòng thiết bị thì thẻ đang trỏ tới nó thành chưa gắn. Liên kết điền sẵn từ đơn bán giữ nguyên
      const rows = kept.map((row) =>
        !row.linkReadOnly && linkKey(row) && toRemove.has(linkKey(row)) ? { ...row, linkedDeviceSerial: null } : row,
      );
      // Thẻ vừa mất máy, hoặc serial ngoài đơn của thẻ vừa xoá, chuyển sang chỗ còn trống
      return { ...state, rows: applyBhmrAutoLink(rows, state.orderLines) };
    }

    case "SET_ROW_QUANTITY":
      return {
        ...state,
        rows: state.rows.map((row) =>
          rowKey(row) === action.serial ? { ...row, primaryQuantity: action.quantity } : row,
        ),
      };

    case "SET_ROW_NOTE":
      return {
        ...state,
        rows: state.rows.map((row) =>
          rowKey(row) === action.serial ? { ...row, note: action.note } : row,
        ),
      };

    case "LINK_DEVICE": {
      const cardKey = action.cardSerial.trim();
      const deviceKey = action.deviceSerial.trim();
      const card = state.rows.find((row) => rowKey(row) === cardKey);
      const device = state.rows.find((row) => rowKey(row) === deviceKey);
      if (!card?.isWarrantyCard || card.linkReadOnly || !device || device.isWarrantyCard || device.isNoSerial) {
        return state;
      }
      // Thiết bị đang thuộc liên kết điền sẵn từ đơn bán thì không lấy sang thẻ khác được
      const holders = state.rows.filter((row) => row !== card && row.isWarrantyCard && linkKey(row) === deviceKey);
      if (holders.some((row) => row.linkReadOnly)) return state;
      // Mỗi thiết bị chỉ gắn một thẻ: gỡ ở thẻ đang giữ thiết bị này, thẻ đó tự nhận máy khác còn trống
      const rows = state.rows.map((row) => {
        if (row === card) return { ...row, linkedDeviceSerial: deviceKey, autoLinkOff: false };
        return holders.includes(row) ? { ...row, linkedDeviceSerial: null } : row;
      });
      return { ...state, rows: applyBhmrAutoLink(rows, state.orderLines) };
    }

    case "UNLINK_DEVICE": {
      const cardKey = action.cardSerial.trim();
      const card = state.rows.find((row) => rowKey(row) === cardKey);
      if (!card || card.linkReadOnly || !linkKey(card)) return state;
      // Thẻ gỡ tay không tự gắn lại; máy vừa gỡ có thể sang thẻ trống khác
      const rows = state.rows.map((row) =>
        row === card ? { ...row, linkedDeviceSerial: null, autoLinkOff: true } : row,
      );
      return { ...state, rows: applyBhmrAutoLink(rows, state.orderLines) };
    }

    case "SET_EXCHANGE_SERIALS": {
      const key = action.serial.trim();
      // Một đổi một: chỉ giữ serial đầu tiên
      const oldSerials = Array.from(new Set(action.oldSerials.map(normalizeExchangeSerial).filter(Boolean))).slice(0, 1);
      return {
        ...state,
        rows: state.rows.map((row) => (rowKey(row) === key ? { ...row, exchangeOldSerials: oldSerials } : row)),
      };
    }

    case "SAVED":
      return {
        ...state,
        transactionId: action.transactionId || state.transactionId,
        header: { ...state.header, status: "OPEN" },
      };

    case "LOCKED":
      return {
        ...state,
        transactionId: action.transactionId || state.transactionId,
        header: { ...state.header, status: "LOCK", finishDate: action.finishDate },
      };

    default:
      return state;
  }
};
